// src/components/ModernStats.js
import React, { useState } from 'react';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    Tabs,
    Tab,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    BarChart as BarChartIcon,
    PieChart as PieChartIcon,
    Timeline as TimelineIcon,
    Description as DescriptionIcon,
    Chat as ChatIcon,
    ThumbUp as ThumbUpIcon,
    MenuBook as MenuBookIcon
} from '@mui/icons-material';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

/**
 * visits: array of objects { visit_date: 'YYYY-MM-DD', work_id: 'uuid', count: number }
 * works: array of work objects with at least { id, title_en }
 */
const ModernStats = ({ works = [], stories = {}, comments = {}, visits = [] }) => {
    const [statsTab, setStatsTab] = useState(0);

    // Likes trend data: use actual work titles
    const likeTrendData = {
        labels: works.map(w => w.title_en || 'Untitled'),
        datasets: [
            {
                label: 'Likes',
                data: works.map(w => w.likes || 0),
                borderColor: '#3f51b5',
                backgroundColor: 'rgba(63, 81, 181, 0.2)',
                tension: 0.3,
                fill: true
            }
        ]
    };

    const likeTrendOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'top' },
            title: { display: true, text: 'Likes by Work' }
        },
        scales: {
            x: { title: { display: true, text: 'Work Title' } },
            y: { title: { display: true, text: 'Likes' }, beginAtZero: true }
        }
    };

    // Engagement data
    const engagementData = {
        labels: works.map(w => w.title_en || 'Untitled'),
        datasets: [
            {
                label: 'Likes',
                data: works.map(w => w.likes || 0),
                backgroundColor: 'rgba(63, 81, 181, 0.6)'
            },
            {
                label: 'Comments',
                data: works.map(w => (comments[w.id] || []).length),
                backgroundColor: 'rgba(0, 150, 136, 0.6)'
            }
        ]
    };

    const engagementOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'top' },
            title: { display: true, text: 'Work-Based Likes & Comments' }
        },
        scales: {
            x: { title: { display: true, text: 'Work Title' } },
            y: { title: { display: true, text: 'Count' }, beginAtZero: true }
        }
    };

    // Top 5 works by likes
    const sortedByLikes = [...works]
        .sort((a, b) => (b.likes || 0) - (a.likes || 0))
        .slice(0, 5);

    return (
        <Box>
            {/* KPI Banner */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                    <Card elevation={2}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <DescriptionIcon sx={{ fontSize: 32, color: '#3f51b5' }} />
                            <Typography variant="subtitle2" color="text.secondary">Total Works</Typography>
                            <Typography variant="h5">{works.length}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card elevation={2}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <ChatIcon sx={{ fontSize: 32, color: '#009688' }} />
                            <Typography variant="subtitle2" color="text.secondary">Total Comments</Typography>
                            <Typography variant="h5">{Object.values(comments).flat().length}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card elevation={2}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <ThumbUpIcon sx={{ fontSize: 32, color: '#f44336' }} />
                            <Typography variant="subtitle2" color="text.secondary">Total Likes</Typography>
                            <Typography variant="h5">{works.reduce((sum, w) => sum + (w.likes || 0), 0)}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card elevation={2}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <MenuBookIcon sx={{ fontSize: 32, color: '#ff9800' }} />
                            <Typography variant="subtitle2" color="text.secondary">Total Stories</Typography>
                            <Typography variant="h5">{Object.values(stories).flat().length}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Tabs */}
            <Tabs
                value={statsTab}
                onChange={(e, v) => setStatsTab(v)}
                textColor="primary"
                indicatorColor="primary"
                sx={{ mb: 2 }}
            >
                <Tab icon={<TimelineIcon />} label="Trends" />
                <Tab icon={<BarChartIcon />} label="Engagement" />
                <Tab icon={<PieChartIcon />} label="Traffic" />
            </Tabs>

            {/* Trends Tab */}
            {statsTab === 0 && (
                <Box sx={{ height: 400, mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1, textAlign: 'center' }}>Likes by Work</Typography>
                    <Box sx={{ height: 350, position: 'relative' }}>
                        <Line data={likeTrendData} options={likeTrendOptions} />
                    </Box>
                </Box>
            )}

            {/* Engagement Tab */}
            {statsTab === 1 && (
                <Box>
                    <Box sx={{ height: 400, mb: 3 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1, textAlign: 'center' }}>Work-Based Likes & Comments</Typography>
                        <Box sx={{ height: 350, position: 'relative' }}>
                            <Bar data={engagementData} options={engagementOptions} />
                        </Box>
                    </Box>
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>Top 5 Works Details</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={1}>
                                {sortedByLikes.map(w => (
                                    <Grid item xs={12} key={w.id}>
                                        <Card elevation={1} sx={{ mb: 1 }}>
                                            <CardContent sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Box>
                                                    <Typography variant="subtitle2">{w.title_en}</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Comments: {(comments[w.id] || []).length} | Likes: {w.likes || 0}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="h6" sx={{ alignSelf: 'center' }}>{w.likes || 0}</Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                </Box>
            )}

            {/* Traffic Tab */}
            {statsTab === 2 && (
                <Box>
                    <Typography variant="subtitle1" sx={{ mb: 1, textAlign: 'center' }}>Visits by Date and Work</Typography>

                    {/* Collapsible Table Panel */}
                    <Accordion sx={{ mb: 3 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>Show/Hide Visits Table</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Work Title</TableCell>
                                        <TableCell align="right">Visits</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {visits
                                        .sort((a, b) => a.visit_date.localeCompare(b.visit_date))
                                        .map((v, idx) => {
                                            const work = works.find(w => w.id === v.work_id);
                                            return (
                                                <TableRow key={idx}>
                                                    <TableCell>{v.visit_date}</TableCell>
                                                    <TableCell>{work ? work.title_en : 'Unknown'}</TableCell>
                                                    <TableCell align="right">{v.count}</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                            </Table>
                        </AccordionDetails>
                    </Accordion>

                    {/* Combined Bar chart: Visits per work by date */}
                    <Box sx={{ height: 450 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1, textAlign: 'center' }}>Visits Per Work Over Time</Typography>
                        <Box sx={{ height: 400, position: 'relative' }}>
                            {(() => {
                                // Collect unique dates
                                const dateSet = new Set();
                                visits.forEach(v => dateSet.add(v.visit_date));
                                const sortedDates = Array.from(dateSet).sort();

                                // Build a map: work_id → { date: count }
                                const workDateMap = {};
                                works.forEach(w => { workDateMap[w.id] = {}; });
                                visits.forEach(v => {
                                    if (!workDateMap[v.work_id]) workDateMap[v.work_id] = {};
                                    workDateMap[v.work_id][v.visit_date] = v.count;
                                });

                                // Build datasets for bar chart: one dataset per work
                                const barDatasets = works.map((w, idx) => {
                                    const dataPoints = sortedDates.map(d => workDateMap[w.id][d] || 0);
                                    return {
                                        label: w.title_en,
                                        data: dataPoints,
                                        backgroundColor: `hsl(${(idx * 360) / works.length}, 70%, 50%)`
                                    };
                                });

                                const barData = {
                                    labels: sortedDates,
                                    datasets: barDatasets
                                };

                                const barOptions = {
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                        legend: { position: 'top', labels: { boxWidth: 12 } },
                                        title: { display: true, text: 'Visits per Work Over Time' }
                                    },
                                    scales: {
                                        x: { title: { display: true, text: 'Date' } },
                                        y: { title: { display: true, text: 'Visits' }, beginAtZero: true }
                                    }
                                };
                                return <Bar data={barData} options={barOptions} />;
                            })()}
                        </Box>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default ModernStats;
